import { Chart, LineController, PointElement, LineElement, LinearScale, Tooltip, type TooltipItem } from "chart.js";
import type { WeightEntry, WeeklyRow } from "./types";
import { dayNumber, formatDayNumber, formatDateLabel } from "./dates";
import { formatWeight } from "./format";
import { readChartTheme } from "./theme";

Chart.register(LineController, PointElement, LineElement, LinearScale, Tooltip);

interface ChartPoint {
	x: number;
	y: number;
	dateLabel?: string;
	weekLabel?: string;
}

// The Chart.js instance is kept in a closure here rather than on the object
// returned to callers — an Alpine reactivity proxy must never wrap it, since
// the controller's internal stats caching breaks subtly when its object
// graph gets proxied.
export function createChartRenderer() {
	let chart: Chart | null = null;
	const theme = readChartTheme();

	return {
		render(entries: WeightEntry[], weeklyRows: WeeklyRow[]): void {
			const canvas = document.getElementById("weight-chart") as HTMLCanvasElement | null;
			if (!canvas || entries.length === 0) return;

			// A note-only entry/week has no weight to plot — skip it rather than
			// feed the chart a gap.
			const pointsData: ChartPoint[] = entries
				.filter((e) => e.weight !== undefined)
				.map((e) => ({
					x: dayNumber(e.date),
					y: e.weight!,
					dateLabel: formatDateLabel(e.date),
				}));
			const medianData: ChartPoint[] = weeklyRows
				.filter((w) => w.median !== null)
				.map((w) => ({
					x: dayNumber(w.weekStart) + 3, // mid-week, so the line reads as a weekly trend
					y: w.median!,
					weekLabel: w.weekLabel,
				}));

			if (chart) {
				chart.data.datasets[0].data = pointsData;
				chart.data.datasets[1].data = medianData;
				chart.update();
				return;
			}

			chart = new Chart(canvas, {
				type: "line",
				data: {
					datasets: [
						{
							label: "Daily entries",
							data: pointsData,
							order: 1, // Chart.js draws higher `order` datasets first, so lower ones land on top
							showLine: false,
							pointRadius: 3,
							pointBackgroundColor: theme.accentFade,
							pointBorderColor: theme.accentFade,
						},
						{
							label: "Weekly median",
							data: medianData,
							order: 0,
							showLine: true,
							borderDash: [6, 4],
							borderColor: theme.ink,
							borderWidth: 2.5,
							pointRadius: 0,
							tension: 0,
							fill: false,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { display: false },
						tooltip: {
							backgroundColor: theme.ink,
							titleFont: { family: theme.fontMono },
							bodyFont: { family: theme.fontMono },
							callbacks: {
								title: () => "",
								label: (ctx: TooltipItem<"line">) => {
									const raw = ctx.raw as { y: number; dateLabel?: string; weekLabel?: string };
									const when = raw.weekLabel ?? raw.dateLabel ?? "";
									return `${when}: ${formatWeight(raw.y)} kg`;
								},
							},
						},
					},
					scales: {
						x: {
							type: "linear",
							grid: { color: theme.rule },
							ticks: {
								font: { family: theme.fontMono, size: 10 },
								callback: (value) => formatDayNumber(Number(value)),
							},
						},
						y: {
							beginAtZero: false,
							grid: { color: theme.rule },
							ticks: { font: { family: theme.fontMono, size: 10 } },
						},
					},
				},
			});
		},
	};
}
